import { bindable, autoinject, Disposable, reset } from 'aurelia-framework';
import {
	IReportTemplateRow,
	IRowGeneratorParameters,
	CommonRowsSet,
	ICommonRow,
	ReportTemplateRowType,
	IFinancialSumRow,
	IPerformanceIndicatorRow
} from '../models/report-template';
import { RowGeneratorCommonRows, ICommonRowOption } from '../models/RowGeneratorCommonRows';
import { SideBar } from '../../../components/side-bar/side-bar';
import { ScopeType } from '../../scopes/scope-type-service';
import { BindingEngine, observable } from 'aurelia-binding';
import { areDeepEqual } from '../../../utils/object-utils';


interface SelectableRow {
	option: ICommonRowOption;
	selectedRow: ICommonRow | undefined;
};
@autoinject
export class RowGeneratorIncludes {
	@bindable isOrganizationalStructure: boolean;
	@bindable parameters: IRowGeneratorParameters;
	@bindable allowChangingCommonRowsSet: boolean;
	@bindable scopeTypes: ScopeType[];
	@bindable scopeTypeIdIsMissing: boolean;
	@bindable showDimensionFiltersOption: boolean;
	/** "Tarkenteet" checkbox next to "Tilit", selectable on its own: offered when the customer has accounting dimensions */
	@bindable showDimensionSeparationOption: boolean;
	@bindable canUseServiceStructure: boolean;
	@bindable change: (params: { editedParameters: IRowGeneratorParameters }) => void;
	selectAll: boolean = false;

	@observable commonRowOptions: SelectableRow[]; // All available options + possible selected rows

	private originalParameters: IRowGeneratorParameters;
	private subscriptions: Disposable[] = [];

	constructor(
		private sideBar: SideBar,
		private bindingEngine: BindingEngine,
		private rowGeneratorCommonRows: RowGeneratorCommonRows) {
	}

	changeCommonRowsSet(set: CommonRowsSet, resetCommonRowSelections: boolean = true) {

		if (!set) { set = CommonRowsSet.Basic; } // Existing reports may not have the set specified. Plus set is temporarily null when loading the view.

		this.parameters.commonRowsSet = set;

		const options = this.rowGeneratorCommonRows.getOptions(set);

		if (resetCommonRowSelections) {
			this.commonRowOptions = options.map(o => ({
				option: o,
				selectedRow: o.isCheckedByDefault
					? {
						rowTemplate: o.row,
						accountGroupSeparation: this.useAccountGroupSeparationByDefault(o),
						accountSeparation: this.useAccountSeparationByDefault(o),
						typeSeparation: this.useTypeSeparationByDefault(o)
					}
					: undefined
			}));
			this.parameters.selectedCommonRows = this.commonRowOptions
				.map(o => o.selectedRow)
				.filter(r => !!r);

			// Uncheck select all checkbox when changing tabs
			this.selectAll = false;
		}
		else {
			this.commonRowOptions = options.map(o => ({
				option: o,
				selectedRow: this.findSelectedCommonRow(o)
			}));
		}
	}

	handleKeypress(e: KeyboardEvent, set: CommonRowsSet) {
		e.stopPropagation();

		if (e.code === "Space" || e.code === "Enter") {
			this.changeCommonRowsSet(set);
		}
	}

	attached() {
		this.originalParameters = Object.assign({}, this.parameters);

		this.sideBar.addCloseEventListener(this.resetParameters);
		this.syncSubscriptions(this.parameters);
	}

	private syncSubscriptions(params: IRowGeneratorParameters) {
		this.subscriptions.forEach(sub => { sub.dispose(); });

		if (params == null) { return; }

		const validateScopeTypeId = () => {
			if (this.scopeTypeIdIsMissing) {
				if (this.parameters.scopeTypeId
					|| (!this.parameters.includeMeasures && !this.parameters.includePerformanceIndicators)) {
					this.scopeTypeIdIsMissing = false;
				}
			}
		};

		this.subscriptions = Object.keys(params)
			.filter(key => key !== 'commonRows')
			.map(key => this.bindingEngine.expressionObserver(params, key)
				.subscribe((newValue) => {
					validateScopeTypeId();

					if (!this.change) { return; }
					const newIncludes = {};
					newIncludes[key] = newValue;
					this.change({ editedParameters: Object.assign({}, params, newIncludes) });
				})
			);
	}

	private resetParameters = (() => {
		this.parameters = Object.assign({}, this.originalParameters);
	});

	detached() {
		this.sideBar.removeCloseEventListener(this.resetParameters);
		this.subscriptions.forEach(sub => { sub.dispose(); });
	}

	parametersChanged(newValue: IRowGeneratorParameters) {
		if (!!newValue) {
			this.originalParameters = Object.assign({}, this.parameters);
			this.changeCommonRowsSet(newValue.commonRowsSet, false); // don't change existing selections when populating whole parameter set
			this.syncSubscriptions(newValue);
		}
	}

	/*** If the common row indicated by this option is selected, return it */
	findSelectedCommonRow(option: ICommonRowOption) {
		const result = this.parameters.selectedCommonRows.find(cr => this.commonRowMatcher(cr, option.row));
		return result;
	}

	commonRowMatcher = (a: ICommonRow, b: IReportTemplateRow) => {
		if (a == null || b == null) {
			return false;
		}

		// T_TOIMKULUT type change made with income statement
		if (b.type === ReportTemplateRowType.FinancialSum &&
			(b as IFinancialSumRow).performanceIndicator === 'T_TOIMKULUT' &&
			a.rowTemplate.type === ReportTemplateRowType.PerformanceIndicator &&
			(a.rowTemplate as IPerformanceIndicatorRow).code === 'T_TOIMKULUT') {
			return true;
		}

		if (a.rowTemplate.type !== b.type) {
			return false;
		}

		return areDeepEqual(a.rowTemplate, b);
	}

	useAccountGroupSeparationByDefault(rowOption: ICommonRowOption) {
		return useAccountGroupSeparationByDefault(rowOption, this.parameters.commonRowsSet);
	}

	useAccountSeparationByDefault(rowOption: ICommonRowOption) {
		return useAccountSeparationByDefault(rowOption, this.parameters.commonRowsSet);
	}

	useTypeSeparationByDefault(rowOption: ICommonRowOption) {
		return useTypeSeparationAvailableByDefault(rowOption, this.parameters.commonRowsSet);
	}

	changeCommonRow(row: SelectableRow, checked: boolean) {
		if (checked) {
			if (!row.selectedRow) {
				row.selectedRow = {
					rowTemplate: row.option.row,
					accountGroupSeparation: this.useAccountGroupSeparationByDefault(row.option),
					accountSeparation: this.useAccountSeparationByDefault(row.option),
					typeSeparation: this.useTypeSeparationByDefault(row.option)
				};
			}
			this.parameters.selectedCommonRows = this.commonRowOptions.map(o => o.selectedRow).filter(r => !!r);
			this.selectAll = this.checkSelection();
		}
		else {
			row.selectedRow = undefined;
			this.parameters.selectedCommonRows = this.commonRowOptions.map(o => o.selectedRow).filter(r => !!r);

			if (row.option.includeInSelectAll) {
				this.selectAll = false; // Uncheck select all when unselecting a row
			}
		}
	}

	changeCommonRowSubSelection(row: SelectableRow, checked: boolean) {
		if (checked) {
			this.selectAll = this.checkSelection();
		}
		else { 
			if (row.option.includeInSelectAll) {
				this.selectAll = false; // Uncheck select all when unselecting a row
			}
		}
	}

	// return true if all checkboxes included in select all are checked
	private checkSelection() {
		const checkBoxesToSelect: HTMLInputElement[] = Array.from(document.querySelectorAll(".select-all"));
		return !checkBoxesToSelect.some(cb => cb.checked == false);
	}

	toggleSelectAll(checked: boolean) {
		this.selectAll = checked;
		const checkBoxesToSelect: HTMLInputElement[] = Array.from(document.querySelectorAll(".select-all"));
		const rowsToSelect = this.commonRowOptions.filter(row => row.option.includeInSelectAll);

		if (checked) {
			checkBoxesToSelect.forEach(cb => {
				cb.checked = true;
			});
			rowsToSelect.forEach(row => {
				// Order is important here: row.selectedRow is undefined before calling changeCommonRow()
				this.changeCommonRow(row, true);
				if (row.option.accountGroupSeparationAvailable === true) {
					row.selectedRow.accountGroupSeparation = true;
				}
				if (row.option.accountSeparationAvailable === true) {
					row.selectedRow.accountSeparation = true;
				}
			});
		} else {
			checkBoxesToSelect.forEach(cb => {
				cb.checked = false;
			});
			rowsToSelect.forEach(row => {
				// Order is important here: changeCommonRow() will set row.selectedRow to undefined, so we have to
				// reset the accountGroupSeparation and AccountSeparation flags first
				if (row.option.accountGroupSeparationAvailable === true) {
					row.selectedRow.accountGroupSeparation = false;
				}
				if (row.option.accountSeparationAvailable === true) {
					row.selectedRow.accountSeparation = false;
					row.selectedRow.dimensionSeparation = false;
				}
				this.changeCommonRow(row, false);
			});
		}	
	}
}

export function useAccountGroupSeparationByDefault(rowOption: ICommonRowOption, rowSet: CommonRowsSet) {
	return rowSet === CommonRowsSet.IncomeStatement && !!rowOption.accountGroupSeparationAvailable;
}

export function useAccountSeparationByDefault(rowOption: ICommonRowOption, rowSet: CommonRowsSet) {
	return false;
}

export function useTypeSeparationAvailableByDefault(rowOption: ICommonRowOption, rowSet: CommonRowsSet) {
	return false;
}
